import React, { useState, useEffect, useCallback, useRef } from 'react';
import { font } from '../theme';
import { Card, Row, Col, Progress, Tag, Table, Space, Typography, Button, Tooltip, message } from 'antd';
import {
  DashboardOutlined, CloudServerOutlined, DatabaseOutlined,
  HddOutlined, ClockCircleOutlined, ReloadOutlined,
  CheckCircleOutlined, SyncOutlined, SaveOutlined,
  SwapOutlined, ApiOutlined, ThunderboltOutlined,
  WarningOutlined, DollarOutlined, CloudOutlined,
  SearchOutlined, FireOutlined, InfoCircleOutlined,
  LoadingOutlined, CopyOutlined, ContainerOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { monitoringAPI, opensearchAPI } from '../services/api';
import { brand, gray, state } from '../theme/tokens';
import { useFillHeight } from '../hooks/useViewportFit';
import { fmtDate } from '../utils/date';

/* ─── 초기 로딩 스플래시 ─── */
const MonitoringLoadingScreen: React.FC<{ steps: { label: string; done: boolean; active: boolean }[] }> = ({ steps }) => {
  const doneCount = steps.filter(s => s.done).length;
  const progress = Math.round((doneCount / steps.length) * 100);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #0f172a 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 0,
    }}>
      {/* 배경 격자 패턴 */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(rgba(99,102,241,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.8) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      {/* 중앙 글로우 */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -60%)',
        width: 600, height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* 로고 영역 */}
      <div style={{ position: 'relative', textAlign: 'center', marginBottom: 48 }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20, margin: '0 auto 16px',
          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 40px rgba(99,102,241,0.5), 0 0 80px rgba(99,102,241,0.2)',
        }}>
          <DashboardOutlined style={{ fontSize: 32, color: '#fff' }} />
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
          서버 모니터링
        </div>
        <div style={{ fontSize: 13, color: '#818cf8', marginTop: 4 }}>
          SAPPT Infrastructure Dashboard
        </div>
      </div>

      {/* 프로그레스 바 */}
      <div style={{ width: 380, marginBottom: 32, position: 'relative' }}>
        <div style={{
          height: 4, background: 'rgba(255,255,255,0.06)',
          borderRadius: 99, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 99,
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
            width: `${progress}%`,
            transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: '0 0 12px rgba(99,102,241,0.8)',
          }} />
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.3)',
        }}>
          <span>데이터 수집 중</span>
          <span style={{ color: '#818cf8', fontWeight: 600 }}>{progress}%</span>
        </div>
      </div>

      {/* 단계 목록 */}
      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((step, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 16px', borderRadius: 12,
            background: step.active
              ? 'rgba(99,102,241,0.15)'
              : step.done
                ? 'rgba(16,185,129,0.08)'
                : 'rgba(255,255,255,0.03)',
            border: `1px solid ${step.active ? 'rgba(99,102,241,0.4)' : step.done ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)'}`,
            transition: 'all 0.3s ease',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: step.done
                ? 'rgba(16,185,129,0.2)'
                : step.active
                  ? 'rgba(99,102,241,0.3)'
                  : 'rgba(255,255,255,0.05)',
            }}>
              {step.done ? (
                <CheckCircleOutlined style={{ fontSize: 14, color: '#10b981' }} />
              ) : step.active ? (
                <LoadingOutlined style={{ fontSize: 14, color: '#818cf8' }} spin />
              ) : (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
              )}
            </div>
            <span style={{
              fontSize: 13, fontWeight: step.active ? 600 : 400,
              color: step.done ? '#6ee7b7' : step.active ? '#c7d2fe' : 'rgba(255,255,255,0.35)',
              flex: 1,
              transition: 'color 0.3s ease',
            }}>
              {step.label}
            </span>
            {step.done && (
              <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>완료</span>
            )}
            {step.active && (
              <span style={{ fontSize: 11, color: '#818cf8' }}>진행 중...</span>
            )}
          </div>
        ))}
      </div>

      {/* 하단 힌트 */}
      <div style={{ position: 'absolute', bottom: 32, fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>
        실시간 서버 상태를 수집하고 있습니다
      </div>
    </div>
  );
};

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatUptime = (seconds: number) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  return `${hours}h ${mins}m`;
};

const getSeverity = (percent: number): { color: string; bg: string; label: string } => {
  if (percent < 50) return { color: state.success, bg: state.successSoft, label: 'Normal' };
  if (percent < 75) return { color: state.warning, bg: state.warningSoft, label: 'Warning' };
  if (percent < 90) return { color: '#f97316', bg: '#fff7ed', label: 'High' };
  return { color: '#ef4444', bg: '#fef2f2', label: 'Critical' };
};

interface ServerStatus {
  cpu: { percent: number; count: number; per_core: number[]; load_avg: number[] };
  memory: { total: number; used: number; available: number; percent: number; cached: number; buffers: number };
  swap: { total: number; used: number; percent: number };
  disk: { total: number; used: number; free: number; percent: number };
  network: { bytes_sent: number; bytes_recv: number; packets_sent: number; packets_recv: number; errin: number; errout: number };
  uptime_seconds: number;
  containers: { name: string; status: string; image: string; runtime?: string }[];
  processes: { pid: number; name: string; cpu: number; mem: number; status: string }[];
  database: { size: number; connections: number; tables: { name: string; rows: number }[] };
  backup: { last_backup: string | null; backup_count: number; total_size: number };
  timestamp: string;
}

/* ─── Gauge Ring Component ─── */
const GaugeRing: React.FC<{
  percent: number; label: string; icon: React.ReactNode;
  sub?: string; size?: number;
}> = ({ percent, label, icon, sub, size = 120 }) => {
  const sev = getSeverity(percent);
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <Progress
          type="dashboard"
          percent={percent}
          size={size}
          strokeColor={sev.color}
          trailColor="#f1f5f9"
          strokeWidth={10}
          format={() => (
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: sev.color, lineHeight: 1 }}>{percent}%</div>
              <div style={{ fontSize: 10, color: gray[400], marginTop: 2 }}>{sev.label}</div>
            </div>
          )}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <Space size={4}>
          <span style={{ color: brand.primary, fontSize: 13 }}>{icon}</span>
          <Typography.Text strong style={{ fontSize: 13, color: gray[700] }}>{label}</Typography.Text>
        </Space>
        {sub && <div style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
};

interface OciCostData {
  tenancy: string;
  region: string;
  instances: { name: string; shape: string; state: string; ocpus: number; memory_gb: number; created: string }[];
  boot_volumes: { name: string; size_gb: number; state: string; vpus_per_gb: number }[];
  block_volumes: { name: string; size_gb: number; state: string; vpus_per_gb: number }[];
  vcns: { name: string; cidr: string; state: string }[];
  cost_data: { service: string; currency: string; computed_amount: number | null; time_usage_started: string; time_usage_ended: string }[];
  usage_summary: { total_ocpus: number; total_memory_gb: number; total_boot_storage_gb: number; total_block_storage_gb: number };
  free_limits: { ocpus: number; memory_gb: number; boot_volume_gb: number; block_volume_gb: number; object_storage_gb: number; outbound_data_gb: number };
  estimated_monthly_usd: { compute: number; boot_storage: number; block_storage: number; total: number };
  timestamp: string;
}

interface BackupFileInfo {
  name: string; size: number; size_hr: string; mtime: string;
}
interface BackupStatusData {
  local: {
    daily: { files: BackupFileInfo[]; count: number; retention_days: number };
    quick: { files: BackupFileInfo[]; count: number; retention_hours: number };
    weekly: { files: BackupFileInfo[]; count: number; retention_weeks: number };
    last_full: { status: string; time: string | null };
    last_quick: { status: string; time: string | null };
  };
  oci: {
    objects: { name: string; basename: string; path: string; size: number; size_hr: string; created: string }[];
    summary: {
      count: number; total_size: number; total_size_hr: string; free_tier_gb: number; used_pct: number;
      by_prefix?: { prefix: string; count: number; size: number; size_hr: string }[];
    };
    error: string | null; bucket: string; namespace: string; retention_days: number;
    /* 목록은 호스트 크론이 만든 캐시가 원천이라 '언제 것인지'를 화면에 반드시 띄운다 */
    cached_at?: string | null; cache_age_min?: number | null; stale?: boolean; stale_after_min?: number;
  };
  b2?: {
    count: number; total_size: number; total_size_hr: string;
    free_tier_gb: number; used_pct: number; bucket: string; error: string | null;
    files?: { name: string; basename: string; path: string; size: number; size_hr: string; created: string }[];
  };
}

const LOADING_STEPS = [
  { label: '서버 리소스 수집 (CPU / 메모리 / 디스크)' },
  { label: '컨테이너 상태 확인 (Docker)' },
  { label: '데이터베이스 정보 조회 (PostgreSQL)' },
  { label: 'GHCR 이미지 레지스트리 조회' },
  { label: 'OCI 클라우드 인프라 조회' },
  { label: '백업 현황 조회 (로컬 + OCI)' },
];

const MonitoringPage: React.FC = () => {

  /* 문서 세로스크롤 0 정책 — 헤더 바만 고정하고 지표 영역은 내부 스크롤 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(320);
  const [data, setData] = useState<ServerStatus | null>(null);
  const [ociData, setOciData] = useState<OciCostData | null>(null);
  const [ociLoading, setOciLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ghcr, setGhcr] = useState<any>(null);
  const [backupData, setBackupData] = useState<BackupStatusData | null>(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [initDone, setInitDone] = useState(false);
  const [stepState, setStepState] = useState(LOADING_STEPS.map(() => ({ done: false, active: false })));
  const initRef = useRef(false);

  // 초기 로딩 단계 오케스트레이션
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const run = async () => {
      const mark = (idx: number, done: boolean, nextActive: number | null = null) => {
        setStepState(prev => prev.map((s, i) => ({
          done: i < idx ? true : i === idx ? done : s.done,
          active: i === (nextActive ?? -1),
        })));
      };

      mark(-1, false, 0); // 0번 활성화

      try {
        const res = await monitoringAPI.serverStatus();
        mark(0, true, 1);
        // step 1 (컨테이너) — 이미 포함된 데이터
        await new Promise(r => setTimeout(r, 200));
        mark(1, true, 2);
        // step 2 (DB) — 이미 포함된 데이터
        await new Promise(r => setTimeout(r, 200));
        mark(2, true, 3);
        setData(res.data);
        setLastRefresh(new Date());

        // step 3 GHCR
        try {
          const g = await monitoringAPI.ghcr();
          setGhcr(g.data);
        } catch {}
        mark(3, true, 4);

        // step 4 OCI
        setOciLoading(true);
        try {
          const o = await monitoringAPI.ociCost();
          setOciData(o.data);
        } catch {}
        finally { setOciLoading(false); }
        mark(4, true, 5);

        // step 5 백업 현황
        setBackupLoading(true);
        try {
          const b = await monitoringAPI.backupStatus();
          setBackupData(b.data);
        } catch {}
        finally { setBackupLoading(false); }
        mark(5, true);

        await new Promise(r => setTimeout(r, 350));
      } catch {
        mark(0, true, 1);
      }
      setInitDone(true);
    };

    run();
  }, []);

  const fetchOciData = useCallback(async () => {
    setOciLoading(true);
    try {
      const res = await monitoringAPI.ociCost();
      setOciData(res.data);
    } catch {
      // silent
    } finally {
      setOciLoading(false);
    }
  }, []);

  const fetchBackupData = useCallback(async () => {
    setBackupLoading(true);
    try {
      const res = await monitoringAPI.backupStatus();
      setBackupData(res.data);
    } catch {
      // silent
    } finally {
      setBackupLoading(false);
    }
  }, []);

  const fetchGhcr = useCallback(async () => {
    if (!initDone) return;
    try {
      const res = await monitoringAPI.ghcr();
      setGhcr(res.data);
    } catch {}
  }, [initDone]);

  const fetchData = useCallback(async () => {
    if (!initDone) return;
    setLoading(true);
    try {
      const res = await monitoringAPI.serverStatus();
      setData(res.data);
      setLastRefresh(new Date());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [initDone]);

  useEffect(() => {
    if (!initDone) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, initDone]);

  // 초기 로딩 화면
  if (!initDone) {
    const steps = LOADING_STEPS.map((s, i) => ({
      label: s.label,
      done: stepState[i].done,
      active: stepState[i].active,
    }));
    return <MonitoringLoadingScreen steps={steps} />;
  }

  if (!data) return null;

  const cpuSev = getSeverity(data.cpu.percent);
  const memSev = getSeverity(data.memory.percent);
  const diskSev = getSeverity(data.disk.percent);

  const allUp = data.containers.every(c => c.status.toLowerCase().includes('up') || c.status.toLowerCase().includes('running'));
  const healthStatus = allUp && data.cpu.percent < 90 && data.memory.percent < 90;

  /* ─── Per-core bar chart ─── */
  const cpuCoreOption = {
    grid: { top: 8, bottom: 4, left: 4, right: 4, containLabel: true },
    tooltip: { trigger: 'axis' as const, formatter: (p: any) => `Core ${p[0].name}: ${p[0].value}%` },
    xAxis: {
      type: 'category' as const,
      data: data.cpu.per_core.map((_: number, i: number) => `${i}`),
      axisLabel: { fontSize: 10, color: gray[400] },
      axisLine: { show: false }, axisTick: { show: false },
    },
    yAxis: { type: 'value' as const, max: 100, show: false },
    series: [{
      type: 'bar', barWidth: '60%',
      data: data.cpu.per_core.map((v: number) => ({
        value: v,
        itemStyle: { color: getSeverity(v).color, borderRadius: [3, 3, 0, 0] },
      })),
    }],
  };

  /* ─── Memory breakdown donut ─── */
  const usedApp = data.memory.used - (data.memory.cached || 0) - (data.memory.buffers || 0);
  const memDonutOption = {
    tooltip: { trigger: 'item' as const, formatter: (p: any) => `${p.name}: ${formatBytes(p.value)}` },
    series: [{
      type: 'pie', radius: ['55%', '78%'],
      label: { show: false },
      itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
      data: [
        { name: 'Application', value: Math.max(usedApp, 0), itemStyle: { color: '#6366f1' } },
        { name: 'Cached', value: data.memory.cached || 0, itemStyle: { color: '#a5b4fc' } },
        { name: 'Buffers', value: data.memory.buffers || 0, itemStyle: { color: '#c7d2fe' } },
        { name: 'Available', value: data.memory.available, itemStyle: { color: '#f1f5f9' } },
      ],
    }],
  };

  /* ─── Container columns ─── */
  const serviceDescriptions: Record<string, string> = {
    'sappt-postgres': 'PostgreSQL 17 — SAPPT 메인 데이터베이스 (시험일정/계획서/사용자/권한 모든 데이터 저장)',
    'sappt-postgres-exporter': 'Postgres → Prometheus 메트릭 exporter (v0.17.1, PG17 호환)',
    'sappt-tapo-postgres': 'TimescaleDB — TAPO 온습도 시계열 데이터 (sensors.readings 하이퍼테이블)',
    'sappt-tapo-api': 'FastAPI — TAPO 온습도 수집 API (NAS uploader → TimescaleDB)',
    'sappt-opensearch': 'OpenSearch 2.17 — 통합 검색/챗봇 검색 엔진 (한글 형태소, 동의어, 오타 보정)',
    'sappt-prometheus': 'Prometheus v2.53 — 모든 메트릭 수집/저장 (Grafana 데이터 소스)',
    'sappt-grafana': 'Grafana 11 — 대시보드/차트 UI (Prometheus+Loki 시각화)',
    'sappt-loki': 'Loki 3.0 — 로그 집계 저장소 (컨테이너 로그 통합)',
    'sappt-promtail': 'Promtail 3.0 — Docker 컨테이너 로그를 Loki 로 전송',
    'sappt-cadvisor': 'cAdvisor v0.49 — 컨테이너별 CPU/메모리/IO 메트릭 수집',
    'sappt-node-exporter': 'Node Exporter v1.8 — 호스트 시스템 메트릭(CPU/디스크/네트워크)',
    'sappt-registry': 'Docker Registry 2 — k3s 이미지 저장소 (localhost:5000)',
    'nginx-proxy-manager': 'NGINX Proxy Manager — 도메인 라우팅/SSL 인증서(Lets Encrypt)',
    'backend': 'SAPPT Backend (FastAPI) — REST API/인증/권한/파일/챗봇 처리',
    'frontend': 'SAPPT Frontend (React) — 관리자 UI (인디고 테마)',
    'partner-frontend': 'SAPPT Partner Frontend — 파트너사 전용 UI (앰버 테마)',
    'redis': 'Redis 7 — 챗봇 응답 캐시/세션 이력/LLM 쿨다운 공유',
  };
  const getServiceDesc = (name: string): string => {
    if (serviceDescriptions[name]) return serviceDescriptions[name];
    for (const k of Object.keys(serviceDescriptions)) if (name.startsWith(k)) return serviceDescriptions[k];
    return name;
  };

  const containerColumns = [
    {
      title: 'Service', dataIndex: 'name', key: 'name', width: 200,
      render: (v: string) => {
        const short = v.replace('sappt-', '').replace('nginx-proxy-manager', 'nginx-pm');
        return (
          <Tooltip title={getServiceDesc(v)} placement="right">
            <Space size={6} style={{ cursor: 'help' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
              <Typography.Text strong style={{ fontSize: 13 }}>{short}</Typography.Text>
              <InfoCircleOutlined style={{ fontSize: 11, color: gray[400] }} />
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Runtime', dataIndex: 'runtime', key: 'runtime', width: 80,
      render: (v: string) => (
        <Tag color={v === 'k3s' ? 'blue' : 'default'} style={{ borderRadius: 12, fontSize: 11 }}>
          {v === 'k3s' ? 'k3s' : 'Docker'}
        </Tag>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: string) => {
        const isUp = v.toLowerCase().includes('up') || v.toLowerCase().includes('running');
        const isHealthy = v.includes('healthy') || v.includes('Ready: True');
        return (
          <Tag
            icon={isUp ? <CheckCircleOutlined /> : <WarningOutlined />}
            color={isHealthy ? 'success' : isUp ? 'processing' : 'error'}
            style={{ borderRadius: 12, fontSize: 11 }}
          >
            {v}
          </Tag>
        );
      },
    },
    {
      title: 'Image', dataIndex: 'image', key: 'image',
      render: (v: string) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v}</Typography.Text>,
    },
  ];

  /* ─── Process columns ─── */
  const processColumns = [
    { title: 'PID', dataIndex: 'pid', key: 'pid', width: 60,
      render: (v: number) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{v}</Typography.Text> },
    { title: 'Process', dataIndex: 'name', key: 'name',
      render: (v: string) => <Typography.Text strong style={{ fontSize: 12 }}>{v}</Typography.Text> },
    { title: 'CPU %', dataIndex: 'cpu', key: 'cpu', width: 80, align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 50 ? '#ef4444' : v > 10 ? '#f59e0b' : '#64748b', fontWeight: 600, fontSize: 12 }}>{v}</span> },
    { title: 'MEM %', dataIndex: 'mem', key: 'mem', width: 80, align: 'right' as const,
      render: (v: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
          <div style={{ width: 40, height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(v, 100)}%`, height: '100%', background: v > 10 ? '#f59e0b' : '#6366f1', borderRadius: 2 }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: gray[500], minWidth: 32 }}>{v}</span>
        </div>
      ),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 80,
      render: (v: string) => <Tag style={{ fontSize: 10, borderRadius: 8 }} color={v === 'running' ? 'green' : v === 'sleeping' ? 'default' : 'orange'}>{v}</Tag> },
  ];

  /* ─── DB table columns ─── */
  const dbTableColumns = [
    { title: 'Table', dataIndex: 'name', key: 'name',
      render: (v: string) => <Typography.Text code style={{ fontSize: 11 }}>{v}</Typography.Text> },
    { title: 'Rows', dataIndex: 'rows', key: 'rows', width: 100, align: 'right' as const,
      render: (v: number) => <span style={{ fontWeight: 600, fontSize: 12 }}>{(v || 0).toLocaleString()}</span> },
  ];

  return (
    <div>
      {/* ─── Header Bar ─── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, padding: '16px 20px', background: '#fff', borderRadius: 12,
        border: '1px solid #e2e8f0',
      }}>
        <Space size={16}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: healthStatus ? 'linear-gradient(135deg, #10b981, #34d399)' : 'linear-gradient(135deg, #ef4444, #f87171)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CloudServerOutlined style={{ color: '#fff', fontSize: 20 }} />
          </div>
          <div>
            <Typography.Title level={5} style={{ margin: 0, fontSize: 16 }}>
              Server Monitoring
              <Tag color={healthStatus ? 'success' : 'error'} style={{ marginLeft: 10, borderRadius: 12, fontSize: 11 }}>
                {healthStatus ? 'All Systems Operational' : 'Attention Required'}
              </Tag>
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Last updated: {data.timestamp} KST &middot; Auto-refresh: 30s
            </Typography.Text>
          </div>
        </Space>
        <Space>
          <Tooltip title="Load Average (1m / 5m / 15m)">
            <Tag style={{ borderRadius: 8, fontSize: 11, padding: '2px 10px' }}>
              <ThunderboltOutlined /> LA: {data.cpu.load_avg.map(v => v.toFixed(2)).join(' / ')}
            </Tag>
          </Tooltip>
          <Tooltip title={`Uptime: ${formatUptime(data.uptime_seconds)}`}>
            <Tag color="blue" style={{ borderRadius: 8, fontSize: 11, padding: '2px 10px' }}>
              <ClockCircleOutlined /> {formatUptime(data.uptime_seconds)}
            </Tag>
          </Tooltip>
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchData}
            loading={loading}
            size="small"
            style={{ borderRadius: 8 }}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* 지표 카드들은 합계 높이가 뷰포트를 크게 넘는다 → 실측 높이 안에서만 스크롤시킨다.
          (Row 음수 gutter 가 가로 스크롤바를 만들므로 overflowX 는 hidden) */}
      <div ref={fillRef} style={{ height: fillHeight, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ─── Resource Gauges ─── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 12, border: `1px solid ${cpuSev.color}22` }}>
            <Row gutter={16} align="middle">
              <Col span={10}>
                <GaugeRing
                  percent={data.cpu.percent}
                  label="CPU"
                  icon={<DashboardOutlined />}
                  sub={`${data.cpu.count} Cores`}
                  size={110}
                />
              </Col>
              <Col span={14}>
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>Per-Core Usage</Typography.Text>
                </div>
                <ReactECharts option={cpuCoreOption} style={{ height: 80 }} opts={{ renderer: 'svg' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <Typography.Text style={{ fontSize: 10, color: gray[400] }}>
                    Load: {data.cpu.load_avg[0].toFixed(2)}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 10, color: gray[400] }}>
                    {data.cpu.per_core.length} threads
                  </Typography.Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 12, border: `1px solid ${memSev.color}22` }}>
            <Row gutter={16} align="middle">
              <Col span={10}>
                <GaugeRing
                  percent={data.memory.percent}
                  label="Memory"
                  icon={<HddOutlined />}
                  sub={formatBytes(data.memory.total)}
                  size={110}
                />
              </Col>
              <Col span={14}>
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>Memory Breakdown</Typography.Text>
                </div>
                <ReactECharts option={memDonutOption} style={{ height: 90 }} opts={{ renderer: 'svg' }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {[
                    { label: 'Used', color: '#6366f1', value: formatBytes(Math.max(usedApp, 0)) },
                    { label: 'Cache', color: '#a5b4fc', value: formatBytes(data.memory.cached || 0) },
                    { label: 'Free', color: '#e2e8f0', value: formatBytes(data.memory.available) },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: gray[500] }}>
                      <div style={{ width: 6, height: 6, borderRadius: 2, background: item.color }} />
                      {item.label} {item.value}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 12, border: `1px solid ${diskSev.color}22` }}>
            <Row gutter={16} align="middle">
              <Col span={10}>
                <GaugeRing
                  percent={data.disk.percent}
                  label="Disk"
                  icon={<DatabaseOutlined />}
                  sub={formatBytes(data.disk.total)}
                  size={110}
                />
              </Col>
              <Col span={14}>
                <div style={{ marginBottom: 12 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>Storage Details</Typography.Text>
                </div>
                {[
                  { label: 'Used', value: formatBytes(data.disk.used), color: diskSev.color },
                  { label: 'Free', value: formatBytes(data.disk.free), color: '#10b981' },
                  { label: 'Swap', value: data.swap.total > 0 ? `${formatBytes(data.swap.used)} / ${formatBytes(data.swap.total)} (${data.swap.percent}%)` : 'Not configured', color: gray[500] },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f8fafc' }}>
                    <Typography.Text style={{ fontSize: 11, color: gray[500] }}>{item.label}</Typography.Text>
                    <Typography.Text style={{ fontSize: 11, fontWeight: 600, color: item.color }}>{item.value}</Typography.Text>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                  <Typography.Text style={{ fontSize: 11, color: gray[500] }}>DB Size</Typography.Text>
                  <Typography.Text style={{ fontSize: 11, fontWeight: 600, color: '#6366f1' }}>{formatBytes(data.database.size)}</Typography.Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ─── Network I/O ─── */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
        <Row gutter={[24, 0]} align="middle">
          <Col flex="none">
            <Space size={6}>
              <SwapOutlined style={{ color: brand.primary, fontSize: 14 }} />
              <Typography.Text strong style={{ fontSize: 13 }}>Network I/O</Typography.Text>
            </Space>
          </Col>
          {[
            { icon: '▲', label: 'Sent', value: formatBytes(data.network.bytes_sent), color: '#f59e0b' },
            { icon: '▼', label: 'Recv', value: formatBytes(data.network.bytes_recv), color: '#10b981' },
            { icon: '◆', label: 'Packets Out', value: data.network.packets_sent.toLocaleString(), color: '#6366f1' },
            { icon: '◆', label: 'Packets In', value: data.network.packets_recv.toLocaleString(), color: '#0ea5e9' },
            { icon: '✕', label: 'Errors', value: `${data.network.errin + data.network.errout}`, color: data.network.errin + data.network.errout > 0 ? '#ef4444' : '#94a3b8' },
          ].map(item => (
            <Col flex="auto" key={item.label}>
              <div style={{ textAlign: 'center', padding: '4px 0' }}>
                <div style={{ fontSize: 10, color: gray[400], marginBottom: 2 }}>
                  <span style={{ color: item.color }}>{item.icon}</span> {item.label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: gray[700] }}>{item.value}</div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* ─── Containers + DB ─── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          {/* Docker 서비스 */}
          {(() => {
            const dockerContainers = data.containers.filter(c => c.runtime !== 'k3s');
            const dockerRunning = dockerContainers.filter(c => c.status.toLowerCase().includes('up') || c.status.toLowerCase().includes('running')).length;
            const dockerCols = containerColumns.filter(col => col.key !== 'runtime');
            return (
              <Card
                size="small"
                style={{ borderRadius: 12 }}
                title={
                  <Space size={8}>
                    <ContainerOutlined style={{ color: '#0ea5e9' }} />
                    <span style={{ fontSize: 13 }}>Docker Services</span>
                    <Tag style={{ borderRadius: 10, fontSize: 10 }} color={dockerRunning === dockerContainers.length ? 'success' : 'warning'}>
                      {dockerRunning}/{dockerContainers.length} running
                    </Tag>
                  </Space>
                }
              >
                <Table dataSource={dockerContainers} columns={dockerCols} rowKey="name" size="small" pagination={false} style={{ fontSize: 12 }} />
              </Card>
            );
          })()}
        </Col>
        <Col xs={24} lg={12}>
          {/* k3s 서비스 */}
          {(() => {
            const k3sContainers = data.containers.filter(c => c.runtime === 'k3s');
            const k3sRunning = k3sContainers.filter(c => c.status.toLowerCase().includes('up') || c.status.toLowerCase().includes('running')).length;
            const k3sCols = containerColumns.filter(col => col.key !== 'runtime');
            return (
              <Card
                size="small"
                style={{ borderRadius: 12, marginBottom: 16 }}
                title={
                  <Space size={8}>
                    <ApiOutlined style={{ color: brand.primary }} />
                    <span style={{ fontSize: 13 }}>k3s Services</span>
                    <Tag style={{ borderRadius: 10, fontSize: 10 }} color={k3sRunning === k3sContainers.length ? 'success' : 'warning'}>
                      {k3sRunning}/{k3sContainers.length} running
                    </Tag>
                  </Space>
                }
              >
                <Table dataSource={k3sContainers} columns={k3sCols} rowKey="name" size="small" pagination={false} style={{ fontSize: 12 }} />
              </Card>
            );
          })()}
          <Card
            size="small"
            style={{ borderRadius: 12 }}
            title={
              <Space size={8}>
                <DatabaseOutlined style={{ color: brand.primary }} />
                <span style={{ fontSize: 13 }}>Database</span>
                <Tag style={{ borderRadius: 10, fontSize: 10 }} color="blue">
                  {data.database.connections} conn
                </Tag>
              </Space>
            }
          >
            <Table
              dataSource={data.database.tables}
              columns={dbTableColumns}
              rowKey="name"
              size="small"
              pagination={false}
              scroll={{ y: 180 }}
              style={{ fontSize: 12 }}
            />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10,
            }}>
              <Space size={4}>
                <SaveOutlined style={{ color: brand.primary }} />
                <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>Backup</Typography.Text>
              </Space>
              <Space size={12}>
                {data.backup.last_backup ? (
                  <Tag color="success" style={{ borderRadius: 8, fontSize: 11, margin: 0 }}>
                    {data.backup.last_backup}
                  </Tag>
                ) : (
                  <Tag color="warning" style={{ borderRadius: 8, fontSize: 11, margin: 0 }}>No backup</Tag>
                )}
                <Typography.Text style={{ fontSize: 11, color: gray[500] }}>
                  {data.backup.backup_count}files &middot; {formatBytes(data.backup.total_size)}
                </Typography.Text>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ─── Top Processes ─── */}
      <Card
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={
          <Space size={8}>
            <ThunderboltOutlined style={{ color: brand.primary }} />
            <span style={{ fontSize: 13 }}>Top Processes (by Memory)</span>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {data.processes.length} shown
            </Typography.Text>
          </Space>
        }
      >
        <Table
          dataSource={data.processes}
          columns={processColumns}
          rowKey="pid"
          size="small"
          pagination={false}
          scroll={{ y: 180 }}
          style={{ fontSize: 12 }}
        />
      </Card>


      {/* ─── GHCR + OpenSearch ─── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={14}>
      <Card
        size="small"
        style={{ borderRadius: 12 }}
        title={
          <Space size={8}>
            <CloudServerOutlined style={{ color: brand.primary }} />
            <span style={{ fontSize: 13 }}>GHCR — GitHub Container Registry</span>
            {ghcr?.configured && (
              <Tag color={ghcr.configured.token_present ? 'success' : 'default'} style={{ borderRadius: 10, fontSize: 10 }}>
                {ghcr.configured.owner} {ghcr.configured.token_present ? '(auth)' : '(public)'}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Tooltip title="GitHub Actions 자동 빌드 → ghcr.io 업로드 (sappt-backend / sappt-frontend / sappt-partner-frontend)">
            <InfoCircleOutlined style={{ color: gray[400] }} />
          </Tooltip>
        }
      >
        {!ghcr ? (
          <Typography.Text type="secondary">로딩중...</Typography.Text>
        ) : ghcr.error ? (
          <div style={{ padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 12 }}>
            ⚠️ {ghcr.error}
            <div style={{ marginTop: 6, color: '#78350f', fontSize: 11 }}>backend.yaml 에 env <code>GITHUB_OWNER</code> (선택) / <code>GITHUB_TOKEN</code> (private 패키지) 설정 필요</div>
          </div>
        ) : (
          <Table
            dataSource={ghcr.packages || []}
            rowKey="name"
            size="small"
            pagination={false}
            style={{ fontSize: 12 }}
            expandable={{
              expandedRowRender: (record: any) => {
                const manifest = record.manifest || {};
                const history: string[] = record.build_history || [];
                const pullCmd: string = record.pull_cmd || '';

                const tsToKst = (ts: string) => {
                  const utc = new Date(`${ts.slice(0,4)}-${ts.slice(4,6)}-${ts.slice(6,8)}T${ts.slice(8,10)}:${ts.slice(10,12)}:${ts.slice(12,14)}Z`);
                  const kst = new Date(utc.getTime() + 9 * 60 * 60 * 1000);
                  return `${kst.getFullYear()}-${String(kst.getMonth()+1).padStart(2,'0')}-${String(kst.getDate()).padStart(2,'0')} ${String(kst.getHours()).padStart(2,'0')}:${String(kst.getMinutes()).padStart(2,'0')}`;
                };

                const fmtBytes = (b: number) => {
                  if (!b) return '—';
                  if (b >= 1073741824) return `${(b/1073741824).toFixed(1)} GB`;
                  if (b >= 1048576) return `${(b/1048576).toFixed(1)} MB`;
                  return `${(b/1024).toFixed(0)} KB`;
                };

                return (
                  <div style={{ padding: '12px 8px 4px', background: '#f8faff', borderRadius: 8 }}>
                    {/* Pull 명령어 */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: gray[500], fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pull Command</div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: '#0f172a', borderRadius: 8, padding: '8px 14px',
                      }}>
                        <ContainerOutlined style={{ color: '#818cf8', fontSize: 13 }} />
                        <code style={{ flex: 1, fontSize: 12, color: '#e2e8f0', fontFamily: font.mono, letterSpacing: 0.3 }}>{pullCmd}</code>
                        <Button
                          size="small" type="text" icon={<CopyOutlined />}
                          style={{ color: '#818cf8', padding: '0 4px' }}
                          onClick={() => { navigator.clipboard.writeText(pullCmd); message.success('복사되었습니다.'); }}
                        />
                      </div>
                    </div>

                    <Row gutter={16}>
                      {/* 이미지 정보 */}
                      <Col span={8}>
                        <div style={{ fontSize: 11, color: gray[500], fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>이미지 정보 (arm64)</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {[
                            { label: '압축 크기', value: fmtBytes(manifest.size) },
                            { label: '레이어 수', value: manifest.layers ? `${manifest.layers} layers` : '—' },
                            { label: '아키텍처', value: manifest.arch || '—' },
                          ].map(item => (
                            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                              <span style={{ fontSize: 11, color: gray[400] }}>{item.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: gray[700] }}>{item.value}</span>
                            </div>
                          ))}
                          {manifest.digest && (
                            <div style={{ padding: '5px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: 11, color: gray[400], marginBottom: 2 }}>Digest</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <code style={{ fontSize: 10, color: '#6366f1', wordBreak: 'break-all' }}>{manifest.digest.slice(0, 24)}…</code>
                                <Button size="small" type="text" icon={<CopyOutlined style={{ fontSize: 10 }} />}
                                  style={{ color: gray[400], padding: '0 2px', height: 16 }}
                                  onClick={() => { navigator.clipboard.writeText(manifest.digest); message.success('Digest가 복사되었습니다.'); }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* 빌드 이력 */}
                      <Col span={16}>
                        <div style={{ fontSize: 11, color: gray[500], fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          빌드 이력 (최근 {Math.min(history.length, 10)}회 · KST)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {history.slice(0, 10).map((ts, idx) => (
                            <div key={ts} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '5px 10px', borderRadius: 6,
                              background: idx === 0 ? 'rgba(99,102,241,0.06)' : '#fff',
                              border: `1px solid ${idx === 0 ? 'rgba(99,102,241,0.2)' : '#e2e8f0'}`,
                            }}>
                              <div style={{
                                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                background: idx === 0 ? '#6366f1' : '#cbd5e1',
                              }} />
                              <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: idx === 0 ? '#4338ca' : '#475569', fontWeight: idx === 0 ? 600 : 400 }}>
                                {tsToKst(ts)}
                              </span>
                              {idx === 0 && <Tag color="geekblue" style={{ borderRadius: 6, fontSize: 10, margin: 0, padding: '0 6px' }}>latest</Tag>}
                            </div>
                          ))}
                          {history.length === 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>이력 없음</Typography.Text>}
                        </div>
                      </Col>
                    </Row>
                  </div>
                );
              },
              rowExpandable: (record: any) => record.status === 'ok',
            }}
            columns={[
              { title: 'Package', dataIndex: 'name', width: 200, render: (v: string, r: any) => (
                <Space size={6}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.status === 'ok' ? '#10b981' : '#f59e0b' }} />
                  <Typography.Text strong style={{ fontSize: 13 }}>{v}</Typography.Text>
                </Space>
              )},
              { title: 'Status', dataIndex: 'status', width: 90, render: (v: string) => (
                <Tag color={v === 'ok' ? 'success' : v === 'not_found' ? 'default' : 'warning'} style={{ borderRadius: 10, fontSize: 11 }}>{v}</Tag>
              )},
              { title: '최근 빌드 (KST)', dataIndex: 'versions', width: 160, render: (versions: any[]) => {
                const allTags = (versions || []).flatMap((v: any) => v.tags || []);
                const tsTag = allTags.find((t: string) => /^\d{14}$/.test(t));
                if (!tsTag) return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
                const y = tsTag.slice(0,4), mo = tsTag.slice(4,6), d = tsTag.slice(6,8);
                const h = tsTag.slice(8,10), mi = tsTag.slice(10,12), s = tsTag.slice(12,14);
                const utc = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
                const kst = new Date(utc.getTime() + 9 * 60 * 60 * 1000);
                const fmt = `${kst.getFullYear()}-${String(kst.getMonth()+1).padStart(2,'0')}-${String(kst.getDate()).padStart(2,'0')} ${String(kst.getHours()).padStart(2,'0')}:${String(kst.getMinutes()).padStart(2,'0')}`;
                return <span style={{ fontSize: 12, color: brand.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt}</span>;
              }},
              { title: 'Latest Tags', dataIndex: 'versions', render: (versions: any[]) => (
                <Space size={4} wrap>
                  {(versions || []).slice(0, 5).flatMap((v: any) => (v.tags || []).slice(0, 2)).slice(0, 6).map((t: string, i: number) => (
                    <Tag key={i} style={{ borderRadius: 6, fontSize: 10, margin: 0 }}>{t}</Tag>
                  ))}
                  {(!versions || versions.length === 0) && <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>}
                </Space>
              )},
              { title: 'Link', dataIndex: 'url', width: 70, render: (url: string) => url ? (
                <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>열기 ↗</a>
              ) : '—' },
            ]}
          />
        )}
      </Card>
      </Col>
      <Col xs={24} lg={10}>
        <Card
          title={<Space size={6}><SearchOutlined style={{ color: brand.primary }} /> OpenSearch 검색엔진</Space>}
          size="small"
          style={{ borderRadius: 12 }}
        >
          <OpenSearchPanel />
        </Card>
      </Col>
      </Row>

      {/* ─── OCI Cloud Cost & Resources ─── */}
      <Card
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        loading={ociLoading && !ociData}
        title={
          <Space size={8}>
            <CloudOutlined style={{ color: '#f59e0b' }} />
            <span style={{ fontSize: 13 }}>OCI Cloud Resources & Cost</span>
            {ociData && (
              <Tag color={ociData.estimated_monthly_usd.total === 0 ? 'success' : 'warning'} style={{ borderRadius: 10, fontSize: 10 }}>
                {ociData.estimated_monthly_usd.total === 0 ? 'Always Free' : `$${ociData.estimated_monthly_usd.total.toFixed(2)}/mo`}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined spin={ociLoading} />}
            onClick={fetchOciData}
            loading={ociLoading}
            size="small"
            style={{ borderRadius: 8 }}
          >
            Refresh
          </Button>
        }
      >
        {ociData && (
          <>
            {/* Tenancy Info */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <Tag style={{ borderRadius: 8, fontSize: 11, padding: '2px 10px' }}>
                <CloudOutlined /> Tenancy: {ociData.tenancy}
              </Tag>
              <Tag style={{ borderRadius: 8, fontSize: 11, padding: '2px 10px' }}>
                Region: {ociData.region}
              </Tag>
              <Tag style={{ borderRadius: 8, fontSize: 11, padding: '2px 10px' }}>
                Updated: {ociData.timestamp}
              </Tag>
            </div>

            <Row gutter={[16, 16]}>
              {/* Resource Usage vs Free Limits */}
              <Col xs={24} lg={10}>
                <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10 }}>
                  <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                    <DollarOutlined style={{ color: '#f59e0b', marginRight: 6 }} />
                    Free Tier Usage
                  </Typography.Text>
                  {[
                    { label: 'OCPUs', used: ociData.usage_summary.total_ocpus, limit: ociData.free_limits.ocpus, unit: '' },
                    { label: 'Memory', used: ociData.usage_summary.total_memory_gb, limit: ociData.free_limits.memory_gb, unit: 'GB' },
                    { label: 'Boot Volume', used: ociData.usage_summary.total_boot_storage_gb, limit: ociData.free_limits.boot_volume_gb, unit: 'GB' },
                    { label: 'Block Volume', used: ociData.usage_summary.total_block_storage_gb, limit: ociData.free_limits.block_volume_gb, unit: 'GB' },
                  ].map(item => {
                    const pct = item.limit > 0 ? Math.round((item.used / item.limit) * 100) : 0;
                    const sev = getSeverity(pct);
                    return (
                      <div key={item.label} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <Typography.Text style={{ fontSize: 11, color: gray[500] }}>{item.label}</Typography.Text>
                          <Typography.Text style={{ fontSize: 11, fontWeight: 600, color: sev.color }}>
                            {item.used}{item.unit} / {item.limit}{item.unit} ({pct}%)
                          </Typography.Text>
                        </div>
                        <Progress
                          percent={pct}
                          showInfo={false}
                          strokeColor={sev.color}
                          trailColor="#e2e8f0"
                          size="small"
                          style={{ margin: 0 }}
                        />
                      </div>
                    );
                  })}

                  {/* Estimated Cost */}
                  <div style={{
                    marginTop: 14, padding: '10px 12px', background: '#fff', borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography.Text style={{ fontSize: 12, color: gray[500] }}>
                        <DollarOutlined style={{ marginRight: 4 }} />
                        Estimated Monthly Cost
                      </Typography.Text>
                      <Typography.Text style={{
                        fontSize: 18, fontWeight: 800,
                        color: ociData.estimated_monthly_usd.total === 0 ? '#10b981' : '#f59e0b',
                      }}>
                        ${ociData.estimated_monthly_usd.total.toFixed(2)}
                      </Typography.Text>
                    </div>
                    {ociData.estimated_monthly_usd.total === 0 && (
                      <Typography.Text style={{ fontSize: 10, color: '#10b981' }}>
                        All resources within Always Free tier limits
                      </Typography.Text>
                    )}
                    {ociData.estimated_monthly_usd.total > 0 && (
                      <div style={{ marginTop: 6, fontSize: 10, color: gray[400] }}>
                        {ociData.estimated_monthly_usd.compute > 0 && <div>Compute: ${ociData.estimated_monthly_usd.compute.toFixed(2)}</div>}
                        {ociData.estimated_monthly_usd.boot_storage > 0 && <div>Boot Storage: ${ociData.estimated_monthly_usd.boot_storage.toFixed(2)}</div>}
                        {ociData.estimated_monthly_usd.block_storage > 0 && <div>Block Storage: ${ociData.estimated_monthly_usd.block_storage.toFixed(2)}</div>}
                      </div>
                    )}
                  </div>

                  {/* Actual cost from Usage API */}
                  {ociData.cost_data.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <Typography.Text strong style={{ fontSize: 11, color: gray[700] }}>Actual Cost (Usage API)</Typography.Text>
                      {ociData.cost_data.map((c, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11 }}>
                          <span style={{ color: gray[500] }}>{c.service}</span>
                          <span style={{ fontWeight: 600 }}>{c.currency} {(c.computed_amount ?? 0).toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              {/* Instances & Volumes */}
              <Col xs={24} lg={14}>
                {/* Compute Instances */}
                <div style={{ marginBottom: 12 }}>
                  <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    <CloudServerOutlined style={{ color: '#6366f1', marginRight: 6 }} />
                    Compute Instances
                  </Typography.Text>
                  {ociData.instances.map((inst, idx) => (
                    <div key={idx} style={{
                      padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                      border: '1px solid #e2e8f0', marginBottom: 8,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space size={8}>
                          <Typography.Text strong style={{ fontSize: 12 }}>{inst.name}</Typography.Text>
                          <Tag color={inst.state === 'RUNNING' ? 'success' : 'default'} style={{ borderRadius: 10, fontSize: 10 }}>
                            {inst.state}
                          </Tag>
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{inst.shape}</Typography.Text>
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11, color: gray[500] }}>
                        <span>OCPUs: <strong style={{ color: gray[700] }}>{inst.ocpus}</strong></span>
                        <span>Memory: <strong style={{ color: gray[700] }}>{inst.memory_gb}GB</strong></span>
                        <span>Created: {fmtDate(inst.created)}</span>
                      </div>
                    </div>
                  ))}
                  {ociData.instances.length === 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>No instances found</Typography.Text>
                  )}
                </div>

                {/* Storage */}
                <div style={{ marginBottom: 12 }}>
                  <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    <HddOutlined style={{ color: '#6366f1', marginRight: 6 }} />
                    Storage Volumes
                  </Typography.Text>
                  <Table
                    dataSource={[
                      ...ociData.boot_volumes.map(v => ({ ...v, type: 'Boot' })),
                      ...ociData.block_volumes.map(v => ({ ...v, type: 'Block' })),
                    ]}
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string) => <Typography.Text style={{ fontSize: 11 }}>{v}</Typography.Text> },
                      { title: 'Type', dataIndex: 'type', key: 'type', width: 70, render: (v: string) => <Tag style={{ borderRadius: 8, fontSize: 10 }} color={v === 'Boot' ? 'blue' : 'default'}>{v}</Tag> },
                      { title: 'Size', dataIndex: 'size_gb', key: 'size_gb', width: 70, align: 'right' as const, render: (v: number) => <span style={{ fontWeight: 600, fontSize: 11 }}>{v}GB</span> },
                      { title: 'Status', dataIndex: 'state', key: 'state', width: 90, render: (v: string) => <Tag color={v === 'AVAILABLE' ? 'success' : 'default'} style={{ borderRadius: 8, fontSize: 10 }}>{v}</Tag> },
                    ]}
                    rowKey="name"
                    size="small"
                    pagination={false}
                    style={{ fontSize: 11 }}
                  />
                </div>

                {/* VCN */}
                {ociData.vcns.length > 0 && (
                  <div>
                    <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      <ApiOutlined style={{ color: '#6366f1', marginRight: 6 }} />
                      Virtual Cloud Networks
                    </Typography.Text>
                    {ociData.vcns.map((vcn, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <Space size={8}>
                          <Typography.Text strong style={{ fontSize: 11 }}>{vcn.name}</Typography.Text>
                          <Tag style={{ borderRadius: 8, fontSize: 10 }}>{vcn.cidr}</Tag>
                        </Space>
                        <Tag color={vcn.state === 'AVAILABLE' ? 'success' : 'default'} style={{ borderRadius: 8, fontSize: 10 }}>{vcn.state}</Tag>
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* ─── 백업 현황 ─── */}
      <BackupPanel backupData={backupData} backupLoading={backupLoading} onRefresh={fetchBackupData} />
      </div>
    </div>
  );
};

/* ─── OpenSearch Panel ─── */
const OpenSearchPanel: React.FC = () => {
  const [health, setHealth] = useState<any>(null);
  const [popular, setPopular] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [h, p] = await Promise.all([
        opensearchAPI.health(),
        opensearchAPI.popularSearches(7),
      ]);
      setHealth(h.data);
      setPopular(Array.isArray(p.data) ? p.data : []);
    } catch {
      setHealth({ status: 'unavailable' });
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResync = async () => {
    setSyncing(true);
    try {
      await opensearchAPI.resync();
      fetchData();
    } catch {} finally {
      setSyncing(false);
    }
  };

  const statusColor = health?.status === 'green' ? '#10b981' : health?.status === 'yellow' ? '#f59e0b' : '#ef4444';

  return (
    <Row gutter={16}>
      <Col span={12}>
        <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Typography.Text strong>클러스터 상태</Typography.Text>
            <Space>
              <Tag color={health?.status === 'green' ? 'success' : health?.status === 'yellow' ? 'warning' : 'error'}>
                {health?.status || 'loading...'}
              </Tag>
              <Button size="small" icon={<SyncOutlined spin={syncing} />} onClick={handleResync} loading={syncing}>
                재동기화
              </Button>
            </Space>
          </div>
          {health?.indices && Object.entries(health.indices).map(([name, info]: [string, any]) => (
            <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fff', borderRadius: 8, marginBottom: 4, border: '1px solid #e2e8f0' }}>
              <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>{name.replace('sappt-', '')}</Typography.Text>
              <Space size={12}>
                <Typography.Text style={{ fontSize: 12, color: gray[500] }}>{info.docs}건</Typography.Text>
                <Typography.Text style={{ fontSize: 12, color: gray[400] }}>{formatBytes(info.size)}</Typography.Text>
              </Space>
            </div>
          ))}
          {!health?.indices && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {health?.error || 'OpenSearch 연결 확인 중...'}
            </Typography.Text>
          )}
        </div>
      </Col>
      <Col span={12}>
        <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>
            <FireOutlined style={{ color: '#f59e0b', marginRight: 6 }} />
            인기 검색어 (7일)
          </Typography.Text>
          {popular.length > 0 ? popular.map((p: any, i: number) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 12px', background: '#fff', borderRadius: 8, marginBottom: 4, border: '1px solid #e2e8f0' }}>
              <Space size={8}>
                <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: i < 3 ? '#f59e0b' : '#94a3b8', width: 18, textAlign: 'center' as const }}>
                  {i + 1}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 12 }}>{p.query}</Typography.Text>
              </Space>
              <Typography.Text style={{ fontSize: 12, color: gray[400] }}>{p.count}회</Typography.Text>
            </div>
          )) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>검색 데이터가 아직 없습니다.</Typography.Text>
          )}
        </div>
      </Col>
    </Row>
  );
};

/* ─── BackupPanel ─── */
const BackupPanel: React.FC<{ backupData: BackupStatusData | null; backupLoading: boolean; onRefresh: () => void }> = ({ backupData, backupLoading, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<'local' | 'oci' | 'b2'>('local');

  const statusTag = (s: string) => {
    if (s === 'success') return <Tag color="success" style={{ borderRadius: 8, fontSize: 11 }}>✅ 성공</Tag>;
    if (s === 'running') return <Tag color="processing" style={{ borderRadius: 8, fontSize: 11 }}>🔄 실행중</Tag>;
    return <Tag color="default" style={{ borderRadius: 8, fontSize: 11 }}>—</Tag>;
  };

  const fileColumns = [
    { title: '파일명', dataIndex: 'name', key: 'name', ellipsis: true,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, fontFamily: font.mono }}>{v}</Typography.Text> },
    { title: '크기', dataIndex: 'size_hr', key: 'size_hr', width: 80,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, color: gray[500] }}>{v}</Typography.Text> },
    { title: '생성일시', dataIndex: 'mtime', key: 'mtime', width: 130,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, color: gray[400] }}>{v}</Typography.Text> },
  ];

  const ociColumns = [
    { title: '경로', dataIndex: 'path', key: 'path', width: 100,
      render: (v: string) => <Tag style={{ fontSize: 10, borderRadius: 6 }}>{v}</Tag> },
    { title: '파일명', dataIndex: 'basename', key: 'basename', ellipsis: true,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, fontFamily: font.mono }}>{v}</Typography.Text> },
    { title: '크기', dataIndex: 'size_hr', key: 'size_hr', width: 80,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, color: gray[500] }}>{v}</Typography.Text> },
    { title: '업로드', dataIndex: 'created', key: 'created', width: 130,
      render: (v: string) => <Typography.Text style={{ fontSize: 12, color: gray[400] }}>{v}</Typography.Text> },
  ];

  return (
    <Card
      size="small"
      style={{ borderRadius: 12, marginTop: 16 }}
      loading={backupLoading && !backupData}
      title={
        <Space size={8}>
          <SaveOutlined style={{ color: '#6366f1' }} />
          <span style={{ fontSize: 13 }}>백업 현황</span>
          {backupData && (
            <Tag color={backupData.local.last_full.status === 'success' ? 'success' : 'warning'} style={{ borderRadius: 10, fontSize: 10 }}>
              {backupData.local.last_full.status === 'success' ? '정상' : '확인 필요'}
            </Tag>
          )}
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined spin={backupLoading} />} onClick={onRefresh} loading={backupLoading} size="small" style={{ borderRadius: 8 }}>
          Refresh
        </Button>
      }
    >
      {/* ─── 백업 구성도 ─── */}
      <div style={{ marginBottom: 20, padding: '16px 20px', background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', borderRadius: 14, border: '1px solid rgba(99,102,241,0.25)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>
          Backup Architecture
        </div>
        {/* 소스 → 스크립트 → 저장소 흐름 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', rowGap: 12 }}>
          {/* 소스 그룹 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { icon: '🗄️', label: 'PostgreSQL', color: '#6366f1' },
              { icon: '📁', label: 'Source Code', color: '#10b981' },
            ].map(src => (
              <div key={src.label} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', background: 'rgba(255,255,255,0.06)',
                borderRadius: 8, border: `1px solid ${src.color}44`,
                minWidth: 140,
              }}>
                <span style={{ fontSize: 14 }}>{src.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{src.label}</span>
              </div>
            ))}
          </div>

          {/* 화살표 → 스크립트 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 10px' }}>
            <div style={{ width: 40, height: 2, background: 'linear-gradient(90deg, #6366f1, #818cf8)' }} />
            <div style={{ fontSize: 10, color: '#818cf8', marginTop: 3 }}>db_backup.sh</div>
          </div>

          {/* 스크립트 박스 */}
          <div style={{
            padding: '10px 14px', background: 'rgba(99,102,241,0.15)',
            borderRadius: 10, border: '1px solid rgba(99,102,241,0.4)',
            textAlign: 'center', minWidth: 110,
          }}>
            <div style={{ fontSize: 18, marginBottom: 2 }}>⚙️</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#c7d2fe' }}>db_backup.sh</div>
            <div style={{ fontSize: 10, color: '#818cf8', marginTop: 3 }}>KST 03:00 · 12:00</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>일별 2회 자동실행</div>
          </div>

          {/* 화살표 → 로컬 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 10px' }}>
            <div style={{ width: 40, height: 2, background: 'linear-gradient(90deg, #818cf8, #6366f1)' }} />
          </div>

          {/* 로컬 저장소 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { icon: '📅', label: '일별 전체', sub: '7일 보관', color: '#6366f1' },
              { icon: '⚡', label: '빠른 (DB)', sub: '24h 보관', color: '#0ea5e9' },
              { icon: '📆', label: '주간 스냅샷', sub: '4주 보관', color: '#10b981' },
            ].map(dst => (
              <div key={dst.label} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 10px', background: 'rgba(255,255,255,0.05)',
                borderRadius: 8, border: `1px solid ${dst.color}33`,
              }}>
                <span style={{ fontSize: 12 }}>{dst.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>{dst.label}</div>
                  <div style={{ fontSize: 10, color: dst.color }}>{dst.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* 화살표 → 클라우드 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 10px' }}>
            <div style={{ width: 40, height: 2, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
            <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 3 }}>Cloud Upload</div>
          </div>

          {/* 클라우드 저장소 그룹 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* OCI 저장소 */}
            <div style={{
              padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
              borderRadius: 10, border: '1px solid rgba(245,158,11,0.35)',
              textAlign: 'center', minWidth: 130,
            }}>
              <div style={{ fontSize: 16, marginBottom: 2 }}>☁️</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#fde68a' }}>OCI Object Storage</div>
              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>Always Free · 10GB</div>
            </div>
            {/* B2 저장소 */}
            <div style={{
              padding: '8px 12px', background: 'rgba(14,165,233,0.1)',
              borderRadius: 10, border: '1px solid rgba(14,165,233,0.35)',
              textAlign: 'center', minWidth: 130,
            }}>
              <div style={{ fontSize: 16, marginBottom: 2 }}>🪣</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7dd3fc' }}>Backblaze B2</div>
              <div style={{ fontSize: 10, color: '#0ea5e9', marginTop: 2 }}>Free 10GB · US West</div>
            </div>
          </div>

          {/* Discord 알림 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 10px' }}>
            <div style={{ width: 40, height: 2, background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)', transform: 'rotate(30deg)', marginBottom: 6 }} />
          </div>
          <div style={{
            padding: '8px 12px', background: 'rgba(139,92,246,0.1)',
            borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>🔔</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c4b5fd' }}>Discord</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>Webhook 알림</div>
          </div>
        </div>

        {/* 파일명 패턴 */}
        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { pattern: 'sappt_YYYYMMDD_HHMMSS.sql.gz', color: '#6366f1' },
            { pattern: 'sappt_source_YYYYMMDD_HHMMSS.tar.gz', color: '#10b981' },
          ].map(f => (
            <div key={f.pattern} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', background: 'rgba(255,255,255,0.04)',
              borderRadius: 6, border: `1px solid ${f.color}33`,
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
              <code style={{ fontSize: 10, color: '#94a3b8', fontFamily: font.mono }}>{f.pattern}</code>
            </div>
          ))}
        </div>
      </div>

      {backupData && (
        <>
          {/* KPI 요약 행 */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {[
              { label: '전체 백업', value: `${backupData.local.daily.count}개`, sub: `최근 ${backupData.local.daily.retention_days}일`, color: brand.primary, status: backupData.local.last_full.status, time: backupData.local.last_full.time },
              { label: '빠른 백업', value: `${backupData.local.quick.count}개`, sub: `최근 ${backupData.local.quick.retention_hours}h`, color: '#0ea5e9', status: backupData.local.last_quick.status, time: backupData.local.last_quick.time },
              { label: '주간 스냅샷', value: `${backupData.local.weekly.count}개`, sub: `최근 ${backupData.local.weekly.retention_weeks}주`, color: '#10b981', status: '', time: null },
              { label: 'OCI 원격', value: backupData.oci.summary?.count != null ? `${backupData.oci.summary.count}개` : '—', sub: backupData.oci.summary?.total_size_hr || '', color: '#f59e0b', status: backupData.oci.error ? 'error' : 'success', time: null },
              /* B2 는 오류여도 타일을 남긴다 — 감추면 '조회가 깨진 것'과 '백업이 없는 것'을 구분할 수 없다 */
              ...(backupData.b2 ? [{
                label: 'B2 원격',
                value: backupData.b2.error ? '조회 실패' : `${backupData.b2.count}개`,
                sub: backupData.b2.error ? 'rclone 확인 필요' : backupData.b2.total_size_hr,
                color: backupData.b2.error ? '#ef4444' : '#0ea5e9',
                status: backupData.b2.error ? 'error' : 'success', time: null,
              }] : []),
            ].map((item, _idx, arr) => (
              <Col xs={12} sm={arr.length > 4 ? 4 : 6} key={item.label}>
                <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: `1px solid #e2e8f0` }}>
                  <div style={{ fontSize: 11, color: gray[400], marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
                  <div style={{ fontSize: 11, color: gray[500], marginTop: 2 }}>{item.sub}</div>
                  {item.time && <div style={{ fontSize: 10, color: gray[400], marginTop: 4 }}>최근: {item.time}</div>}
                </div>
              </Col>
            ))}
          </Row>

          {/* 원격 스토리지 사용량 바 */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {/* OCI */}
            {backupData.oci.summary?.used_pct != null && (
              <Col xs={24} md={12}>
                <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 4 }}>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                      ☁️ OCI — ap-chuncheon-1
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 12, color: gray[500] }}>
                      {backupData.oci.summary.total_size_hr} / {backupData.oci.summary.free_tier_gb}GB
                    </Typography.Text>
                  </div>
                  <Progress
                    percent={backupData.oci.summary.used_pct}
                    strokeColor={backupData.oci.summary.used_pct < 50 ? '#10b981' : backupData.oci.summary.used_pct < 80 ? '#f59e0b' : '#ef4444'}
                    trailColor="#e2e8f0"
                    size="small"
                    format={pct => <span style={{ fontSize: 11 }}>{pct}%</span>}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: '#92400e' }}>Standard 무료 {backupData.oci.summary.free_tier_gb}GB · {backupData.oci.summary.count}개 파일</span>
                    <span style={{ fontSize: 10, color: gray[400] }}>daily/ {backupData.oci.retention_days}일 보관</span>
                  </div>
                  {backupData.oci.cached_at && (
                    <div style={{ fontSize: 10, marginTop: 4, color: backupData.oci.stale ? '#b91c1c' : gray[400] }}>
                      {backupData.oci.stale
                        ? `⚠ 목록 갱신 멈춤 — 기준 ${backupData.oci.cached_at} (${backupData.oci.cache_age_min}분 전). update_oci_cache.sh 크론을 확인하세요.`
                        : `기준 ${backupData.oci.cached_at}`}
                    </div>
                  )}
                </div>
              </Col>
            )}
            {/* B2 */}
            {backupData.b2 && backupData.b2.error && (
              <Col xs={24} md={12}>
                <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca', height: '100%' }}>
                  <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>🪣 B2 — 사용량 조회 실패</Typography.Text>
                  <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 6, wordBreak: 'break-all' }}>
                    {backupData.b2.error}
                  </div>
                  <div style={{ fontSize: 10, color: gray[500], marginTop: 6 }}>
                    파일 목록은 아래 'B2 백업' 탭에서 확인할 수 있습니다 (목록과 사용량은 별도 호출).
                  </div>
                </div>
              </Col>
            )}
            {backupData.b2 && !backupData.b2.error && (
              <Col xs={24} md={12}>
                <div style={{ padding: '10px 14px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 4 }}>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                      🪣 B2 — US West
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 12, color: gray[500] }}>
                      {backupData.b2.total_size_hr} / {backupData.b2.free_tier_gb}GB
                    </Typography.Text>
                  </div>
                  <Progress
                    percent={backupData.b2.used_pct}
                    strokeColor={backupData.b2.used_pct < 50 ? '#10b981' : backupData.b2.used_pct < 80 ? '#f59e0b' : '#ef4444'}
                    trailColor="#e2e8f0"
                    size="small"
                    format={pct => <span style={{ fontSize: 11 }}>{pct}%</span>}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: '#075985' }}>Free 10GB · {backupData.b2.count}개 파일</span>
                    <span style={{ fontSize: 10, color: gray[400] }}>Backblaze B2</span>
                  </div>
                </div>
              </Col>
            )}
          </Row>

          {/* 탭: 로컬 / OCI / B2 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {(['local', 'oci', 'b2'] as const).map(tab => (
              <Button
                key={tab}
                size="small"
                type={activeTab === tab ? 'primary' : 'default'}
                onClick={() => setActiveTab(tab)}
                style={{ borderRadius: 8, fontSize: 12 }}
              >
                {tab === 'local' ? '🖥️ 로컬 백업' : tab === 'oci' ? '☁️ OCI 백업' : '🪣 B2 백업'}
              </Button>
            ))}
          </div>

          {activeTab === 'local' && (
            <>
              {[
                { title: '전체 백업 (일별)', files: backupData.local.daily.files, color: brand.primary },
                { title: '빠른 백업 (DB전용)', files: backupData.local.quick.files, color: '#0ea5e9' },
                { title: '주간 스냅샷', files: backupData.local.weekly.files, color: '#10b981' },
              ].map(section => (
                <div key={section.title} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: section.color }} />
                    <Typography.Text strong style={{ fontSize: 12 }}>{section.title}</Typography.Text>
                    <Tag style={{ fontSize: 10, borderRadius: 6 }}>{section.files.length}개</Tag>
                  </div>
                  <Table
                    dataSource={section.files}
                    columns={fileColumns}
                    pagination={false}
                    size="small"
                    rowKey="name"
                    style={{ fontSize: 12 }}
                    scroll={{ y: 160 }}
                  />
                </div>
              ))}
            </>
          )}

          {activeTab === 'oci' && (
            backupData.oci.error
              ? <Typography.Text type="danger" style={{ fontSize: 12 }}>OCI 오류: {backupData.oci.error}</Typography.Text>
              : <Table
                  dataSource={backupData.oci.objects}
                  columns={ociColumns}
                  pagination={false}
                  size="small"
                  rowKey="name"
                  style={{ fontSize: 12 }}
                  scroll={{ y: 400 }}
                />
          )}

          {activeTab === 'b2' && (
            backupData.b2?.error
              ? <Typography.Text type="danger" style={{ fontSize: 12 }}>B2 오류: {backupData.b2.error}</Typography.Text>
              : <Table
                  dataSource={backupData.b2?.files || []}
                  columns={ociColumns}
                  pagination={false}
                  size="small"
                  rowKey="name"
                  style={{ fontSize: 12 }}
                  scroll={{ y: 400 }}
                />
          )}
        </>
      )}
    </Card>
  );
};

export default MonitoringPage;
